import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { CartItem } from '@/modelos/comprobantes/comprobante.types';
import { buildLinePricingInputFromCartItem, calculateLineaComprobante } from '@/shared/Comprobante/core/comprobantePricing';
import Tooltip from '../Tooltip/Tooltip';
import { Badge } from '../Badge/Badge';
import { Checkbox } from '../Checkbox';
import '../TableAdvanced/TableAdvanced.css';
import { Package, Trash2 } from 'lucide-react';
import { CampoConSelector } from '../CampoConSelector';
import type { CampoConSelectorOption, CampoConSelectorDensity } from '../CampoConSelector';
import { ColumnVisibilityPanel } from '../ColumnVisibilityPanel';
import type { ColumnDef as PanelColumnDef } from '../ColumnVisibilityPanel';
import { SegmentedToggle } from '../SegmentedToggle';
import { CollapsiblePanel } from '../CollapsiblePanel';

const TIPO_EXISTENCIA_LABELS: Record<string, string> = {
  MERCADERIAS: 'Mercaderías',
  PRODUCTOS_TERMINADOS: 'Productos Terminados',
  SERVICIOS: 'Servicios',
  MATERIAS_PRIMAS: 'Materias Primas',
  ENVASES: 'Envases',
  MATERIALES_AUXILIARES: 'Materiales Auxiliares',
  SUMINISTROS: 'Suministros',
  REPUESTOS: 'Repuestos',
  EMBALAJES: 'Embalajes',
  OTROS: 'Otros',
};

// ─── STORAGE KEY (mismo que el existente para preservar prefs del usuario) ───
const STORAGE_KEY = 'comprobantes_table_columns_config';
const PRICE_DENSITY_STORAGE_KEY = 'comprobantes_table_price_density';
const DEFAULT_PRICE_DENSITY: CampoConSelectorDensity = 'compact';
const PRICE_DENSITY_OPTIONS: Array<{ value: CampoConSelectorDensity; label: string }> = [
  { value: 'extended', label: 'Cómodo' },
  { value: 'medium', label: 'Intermedio' },
  { value: 'compact', label: 'Compacto' },
];

const TRASLADO_DENSITY_STORAGE_KEY = 'guias_bienes_density';
const TRASLADO_DEFAULT_DENSITY: CampoConSelectorDensity = 'medium';
const TRASLADO_NUMERIC_CELL_CLASSES: Record<CampoConSelectorDensity, { button: string; input: string; text: string }> = {
  compact:  { button: 'w-6 h-6', input: 'h-6', text: 'text-[11px]' },
  medium:   { button: 'w-7 h-7', input: 'h-7', text: 'text-xs' },
  extended: { button: 'w-8 h-8', input: 'h-8', text: 'text-sm' },
};

// ─── TYPES ───
type Align = 'left' | 'center' | 'right';

interface ColDef {
  id: string;
  label: string;
  isFixed: boolean;
  isFixedEnd: boolean;
  align: Align;
  width?: string;
  minWidth?: string;
}

/** Formatea con hasta `maxDecimales`, recortando ceros finales innecesarios (mínimo 2 decimales). */
function formatPrecioDinamico(valor: number, maxDecimales: number): string {
  const fijo = valor.toFixed(maxDecimales);
  const [entero, decimales = ''] = fijo.split('.');
  const decimalesSinCeros = decimales.replace(/0+$/, '');
  const minDecimales = Math.min(2, maxDecimales);
  const decimalesFinal = decimalesSinCeros.length < minDecimales
    ? decimalesSinCeros.padEnd(minDecimales, '0')
    : decimalesSinCeros;
  return decimalesFinal ? `${entero}.${decimalesFinal}` : entero;
}

export interface PriceColumnOption {
  columnId: string;
  label: string;
  price: number;
  isBase: boolean;
  hasExplicitPrice: boolean;
}

export interface UnitOption {
  code: string;
  label: string;
  isBase?: boolean;
}

export interface TablaCatalogoProps {
  items: CartItem[];
  maxHeight?: string | number;
  showColumnConfig: boolean;
  onCloseColumnConfig: () => void;
  modo?: 'venta' | 'traslado' | 'inventario';
  productoColumnLabel?: string;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  emptyStateButtonLabel?: string;
  mostrarColumnaAccion?: boolean;
  soloLectura?: boolean;
  /**
   * Nodo del DOM donde pintar el panel "Columnas visibles" (vía portal),
   * para que el padre pueda anidarlo visualmente dentro de otra caja (ej.
   * "Agregar producto"). Si no se pasa, el panel se pinta inline, como
   * bloque propio antes de la tabla (comportamiento por defecto).
   */
  columnPanelContainer?: HTMLElement | null;
  lastAddedProductId?: CartItem['id'] | null;

  // Datos por ítem
  getPriceOptionsForItem: (item: CartItem) => PriceColumnOption[];
  getUnitOptionsForItem: (item: CartItem) => UnitOption[];
  getAlmacenOptionsForItem?: (item: CartItem) => Array<{ id: string; label: string }>;
  onAlmacenChange?: (item: CartItem, almacenId: string) => void;
  getBienesNormalizadosOptionsForItem?: (item: CartItem) => Array<{ subpartidaNacional: string; codigoProductoSunat: string; descripcion?: string }>;

  // Conversión / formato
  convertBaseToDocument: (amount: number) => number;
  formatBaseAsDocument: (amount: number) => string;
  getIgvPercent: (item: CartItem) => number;
  /**
   * Etiqueta corta y ya resuelta del impuesto (ej. "IGV (18.00%)"), buscada
   * contra el catálogo real de afectaciones tributarias — igual que hace el
   * selector de Impuesto en el modal de producto. Si no hay match o no se
   * provee, se cae al nombre que ya trae el ítem.
   */
  getImpuestoLabelForItem?: (item: CartItem) => string | undefined;
  /** Color real de la categoría, tomado del mismo catálogo que usa el modal de producto. */
  getCategoryColorForItem?: (item: CartItem) => string | undefined;
  formatQuantityDisplay: (value: number) => string;

  // Estado draft (controlado desde el padre)
  priceDrafts: Record<string, string>;
  quantityDrafts: Record<string, string>;
  priceErrors: Record<string, string>;

  // Handlers
  onUpdateItem: (id: string, changes: Partial<CartItem>) => void;
  onRemoveItem: (id: string) => void;
  onQuantityChange: (itemId: string, rawValue: string) => void;
  onQuantityBlur: (item: CartItem, rawValue: string) => void;
  onClearQuantityDraft: (itemId: string) => void;
  onPriceChange: (itemId: string, value: string) => void;
  onPriceBlur: (item: CartItem, rawValue: string) => void;
  onPriceOptionChange: (item: CartItem, columnId: string, options: PriceColumnOption[]) => void;
  onUnitChange: (item: CartItem, unitCode: string) => void;
  onAddProduct?: () => void;
  /** true = user enters Precio U. (con IGV); false = user enters Valor U. (sin IGV). */
  precioIncluyeIgv?: boolean;
  /**
   * `false` en contextos donde no existe un módulo de precios detrás
   * (ej. Notas de ingreso/salida) — oculta el aviso de "esto se guardará
   * como tu precio X" en la columna Precio U., que de otro modo sería
   * información incorrecta. Por defecto `true`.
   */
  enablePriceLearning?: boolean;
  stockDisplayMode?: 'salida' | 'ingreso' | 'actual';
  /** Cantidad de decimales a mostrar en la columna "Precio unitario". Por defecto 2. */
  decimalesPrecio?: number;

  /**
   * Habilita edición bidireccional en las celdas Subtotal/Total: al escribir
   * un valor, se recalcula Cantidad (desde Subtotal) o Precio unitario
   * (desde Total). Si no se pasa, ambas celdas quedan de solo lectura,
   * comportamiento idéntico al que ya tenían.
   */
  permitirEdicionImportes?: boolean;
  subtotalDrafts?: Record<string, string>;
  totalDrafts?: Record<string, string>;
  onSubtotalChange?: (itemId: string, value: string) => void;
  onSubtotalBlur?: (item: CartItem, value: string) => void;
  onTotalChange?: (itemId: string, value: string) => void;
  onTotalBlur?: (item: CartItem, value: string) => void;
  /** Símbolo/prefijo de moneda del documento (ej. "S/", "US$"). Por defecto "S/". */
  currencyPrefix?: string;
  mostrarStockPorDefecto?: boolean;
}

// ─── COLUMN DEFINITIONS (preserva los IDs y labels existentes) ───
const FIXED_START: ColDef[] = [
  { id: 'producto',      label: 'Producto',      isFixed: true,  isFixedEnd: false, align: 'left',   minWidth: '280px' },
  { id: 'codigo',        label: 'Código',         isFixed: true,  isFixedEnd: false, align: 'left',   width: '120px'    },
  { id: 'cantidad',      label: 'Cantidad',       isFixed: true,  isFixedEnd: false, align: 'center', width: '140px'    },
  { id: 'unidad',        label: 'Unidad',         isFixed: true,  isFixedEnd: false, align: 'center', minWidth: '140px' },
];

const OPTIONAL_COLS: ColDef[] = [
  { id: 'imagen',         label: 'Imagen',         isFixed: false, isFixedEnd: false, align: 'center', width: '80px'     },
  { id: 'alias',          label: 'Alias',           isFixed: false, isFixedEnd: false, align: 'left',   minWidth: '140px' },
  { id: 'descripcion',    label: 'Descripción',     isFixed: false, isFixedEnd: false, align: 'left',   minWidth: '200px' },
  { id: 'categoria',      label: 'Categoría',       isFixed: false, isFixedEnd: false, align: 'center', width: '130px'    },
  { id: 'marca',          label: 'Marca',           isFixed: false, isFixedEnd: false, align: 'left',   width: '120px'    },
  { id: 'modelo',         label: 'Modelo',          isFixed: false, isFixedEnd: false, align: 'left',   width: '120px'    },
  { id: 'tipoProducto',   label: 'Tipo',            isFixed: false, isFixedEnd: false, align: 'center', width: '100px'    },
  { id: 'tipoExistencia', label: 'Tipo Existencia', isFixed: false, isFixedEnd: false, align: 'left',   minWidth: '140px' },
  { id: 'codigoBarras',   label: 'Cód. Barras',     isFixed: false, isFixedEnd: false, align: 'left',   width: '130px'    },
  { id: 'codigoFabrica',  label: 'Cód. Fábrica',    isFixed: false, isFixedEnd: false, align: 'left',   width: '130px'    },
  { id: 'codigoSunat',    label: 'Cód. SUNAT',      isFixed: false, isFixedEnd: false, align: 'center', width: '130px'    },
  { id: 'stock',          label: 'Stock',           isFixed: false, isFixedEnd: false, align: 'center', width: '90px'     },
  { id: 'precioCompra',   label: 'P. Compra',       isFixed: false, isFixedEnd: false, align: 'right',  width: '110px'    },
  { id: 'descuento',      label: 'Descuento %',     isFixed: false, isFixedEnd: false, align: 'center', width: '120px'    },
  { id: 'peso',           label: 'Peso (kg)',        isFixed: false, isFixedEnd: false, align: 'right',  width: '100px'    },
  { id: 'impuesto',       label: 'Impuesto',        isFixed: false, isFixedEnd: false, align: 'center', minWidth: '140px' },
];

const FIXED_END: ColDef[] = [
  { id: 'precio',   label: 'Precio unitario', isFixed: true, isFixedEnd: true, align: 'right',  minWidth: '180px' },
  { id: 'subtotal', label: 'Subtotal',  isFixed: true, isFixedEnd: true, align: 'right',  width: '110px'    },
  { id: 'total',    label: 'Total',     isFixed: true, isFixedEnd: true, align: 'right',  width: '110px'    },
  { id: 'accion',   label: '',          isFixed: true, isFixedEnd: true, align: 'center', width: '70px'     },
];

const ALL_COLS = [...FIXED_START, ...OPTIONAL_COLS, ...FIXED_END];
const DEFAULT_VISIBLE_OPTIONAL = new Set<string>([]);

const INVENTARIO_STORAGE_KEY = 'inventario_notas_table_columns_config';
const INVENTARIO_DEFAULT_VISIBLE_OPTIONAL = new Set<string>(['stock']);

// ─── COLUMNAS PARA EL PANEL "Columnas visibles" (ColumnVisibilityPanel) ───
const LOCKED_COLUMNS_PANEL: PanelColumnDef[] = [...FIXED_START, ...FIXED_END]
  .filter(col => col.label)
  .map(col => ({ id: col.id, label: col.label }));

const OPTIONAL_COLUMNS_PANEL: PanelColumnDef[] = OPTIONAL_COLS.map(col => ({ id: col.id, label: col.label }));

const TRASLADO_STORAGE_KEY = 'guias_bienes_columns_config';
const TRASLADO_DEFAULT_VISIBLE_OPTIONAL = new Set<string>(['peso']);

const TRASLADO_OPTIONAL_COL_IDS = ['imagen', 'alias', 'categoria', 'marca', 'modelo', 'tipoProducto', 'tipoExistencia', 'codigoBarras', 'codigoFabrica', 'stock', 'peso'];

const TRASLADO_LEAD_COLS: ColDef[] = [
  { id: 'normalizado', label: 'Normalizado', isFixed: false, isFixedEnd: false, align: 'center', width: '120px' },
  { id: 'subpartida',  label: 'Subpartida',  isFixed: false, isFixedEnd: false, align: 'left',   width: '220px' },
  { id: 'codigoSunat', label: 'Cód. SUNAT',  isFixed: false, isFixedEnd: false, align: 'center', width: '130px' },
];

const TRASLADO_EXTRA_COLS: ColDef[] = [
  { id: 'gtin', label: 'GTIN', isFixed: false, isFixedEnd: false, align: 'left', width: '120px' },
];

const TRASLADO_OPTIONAL_COLS: ColDef[] = [
  ...TRASLADO_LEAD_COLS,
  ...TRASLADO_EXTRA_COLS,
  ...OPTIONAL_COLS.filter(c => TRASLADO_OPTIONAL_COL_IDS.includes(c.id)),
];

const TRASLADO_LOCKED_COLUMNS_PANEL: PanelColumnDef[] = [
  ...FIXED_START.map(col => ({ id: col.id, label: col.label })),
  { id: 'accion', label: 'Acción' },
];

const TRASLADO_OPTIONAL_COLUMNS_PANEL: PanelColumnDef[] = TRASLADO_OPTIONAL_COLS.map(col => ({ id: col.id, label: col.label }));

// ─── IGV CONFIG ───
const IGV_LABELS: Record<string, { label: string; className: string }> = {
  GRAVADO:     { label: 'Gravado',     className: 'bg-teal-50 text-teal-700 border border-teal-200'          },
  EXONERADO:   { label: 'Exonerado',   className: 'bg-orange-50 text-orange-600 border border-orange-200'    },
  INAFECTO:    { label: 'Inafecto',    className: 'bg-neutral-100 text-neutral-500 border border-neutral-200' },
  EXPORTACION: { label: 'Exportación', className: 'bg-sky-50 text-sky-600 border border-sky-200'             },
};

// ─── SUB-COMPONENTS ───

const EmptyVal = () => (
  <span className="text-[11px] italic text-[var(--color-neutral-400,#94a3b8)]">—</span>
);

const InlineBadge = ({ children, className }: { children: React.ReactNode; className: string }) => (
  <span className={`inline-flex items-center h-5 px-1.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap ${className}`}>
    {children}
  </span>
);

const MonoBadge = ({ children, dim = false }: { children: string; dim?: boolean }) => (
  <span className={`font-mono text-[11px] font-medium px-1.5 py-0.5 rounded border inline-block ${
    dim
      ? 'text-slate-400 bg-slate-50 border-slate-200'
      : 'text-slate-500 bg-slate-100 border-slate-200'
  }`}>
    {children}
  </span>
);

const StockBadge = ({ item, mode = 'salida' }: { item: CartItem; mode?: 'salida' | 'ingreso' | 'actual' }) => {
  const stock = item.stock;
  if (stock === null || stock === undefined) return <EmptyVal />;
  const s = Number(stock);
  const qty = item.quantity || 0;

  if (mode === 'ingreso') {
    const resultante = Math.round((s + qty) * 100) / 100;
    return (
      <span className="inline-flex items-center gap-1 h-[22px] px-2 rounded font-mono text-[11px] font-semibold border bg-green-100 text-green-700 border-green-200">
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {resultante}
      </span>
    );
  }

  const remaining = mode === 'actual' ? s : Math.round((s - qty) * 100) / 100;
  const isOut = remaining <= 0;
  const isLow = !isOut && (
    typeof item.stockMinimo === 'number' ? remaining <= item.stockMinimo : remaining <= 10
  );
  const cls = isOut
    ? 'bg-red-50 text-red-600 border-red-200'
    : isLow
      ? 'bg-amber-100 text-amber-600 border-amber-200'
      : 'bg-green-100 text-green-700 border-green-200';
  return (
    <span className={`inline-flex items-center gap-1 h-[22px] px-2 rounded font-mono text-[11px] font-semibold border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {isOut ? 'Agotado' : remaining}
    </span>
  );
};

const DeleteConfirm = ({ onConfirm }: { onConfirm: () => void }) => {
  return (
    <div className="flex items-center justify-center">
      <Tooltip text="Eliminar de la lista" position="left">
        <button
          onClick={onConfirm}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 text-muted hover:bg-error-bg hover:text-error"
        >
          <Trash2 size={16} />
        </button>
      </Tooltip>
    </div>
  );
};

// ─── EMPTY STATE ───
const EmptyState = ({
  colSpan,
  onAdd,
  title = 'No hay productos en el comprobante',
  subtitle = 'Busca, agrega o crea productos desde el buscador de arriba.',
  buttonLabel = 'Agregar primer producto',
}: {
  colSpan: number;
  onAdd?: () => void;
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
}) => (
  <tr>
    <td colSpan={colSpan}>
      <div className="py-12 flex flex-col items-center gap-3 text-center">
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-surface-2 text-tertiary">
          <Package size={20} />
        </div>
        <p className="text-h5 font-display text-primary">{title}</p>
        <p className="text-body-sm text-tertiary max-w-xs">{subtitle}</p>
        {onAdd && (
          <button onClick={onAdd}
            className="inline-flex items-center gap-1.5 h-[34px] px-4 bg-surface-2 hover:bg-surface-3 text-secondary text-[13px] font-medium rounded-lg transition-colors duration-150">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {buttonLabel}
          </button>
        )}
      </div>
    </td>
  </tr>
);

// ─── MAIN COMPONENT ───
export const TablaCatalogo: React.FC<TablaCatalogoProps> = ({
  items,
  maxHeight = 'calc(100vh - 28rem)',
  showColumnConfig,
  onCloseColumnConfig,
  columnPanelContainer = null,
  lastAddedProductId,
  modo = 'venta',
  productoColumnLabel,
  emptyStateTitle,
  emptyStateSubtitle,
  emptyStateButtonLabel,
  mostrarColumnaAccion = true,
  soloLectura = false,
  getPriceOptionsForItem,
  getUnitOptionsForItem,
  getAlmacenOptionsForItem,
  onAlmacenChange,
  getBienesNormalizadosOptionsForItem,
  convertBaseToDocument,
  formatBaseAsDocument,
  getIgvPercent,
  getImpuestoLabelForItem,
  getCategoryColorForItem,
  formatQuantityDisplay,
  priceDrafts,
  quantityDrafts,
  priceErrors,
  onUpdateItem,
  onRemoveItem,
  onQuantityChange,
  onQuantityBlur,
  onClearQuantityDraft,
  onPriceChange,
  onPriceBlur,
  onPriceOptionChange,
  onUnitChange,
  onAddProduct,
  precioIncluyeIgv = true,
  enablePriceLearning = true,
  stockDisplayMode = 'salida',
  decimalesPrecio = 2,
  permitirEdicionImportes = false,
  subtotalDrafts = {},
  totalDrafts = {},
  onSubtotalChange,
  onSubtotalBlur,
  onTotalChange,
  onTotalBlur,
  currencyPrefix = 'S/',
  mostrarStockPorDefecto = false,
}) => {

  const esModoTraslado = modo === 'traslado';
  const esModoInventario = modo === 'inventario';

  // ── Column visibility (gestionado internamente, persistido en localStorage) ──
  const [visibleOptional, setVisibleOptional] = useState<Set<string>>(() => {
    const storageKey = esModoTraslado ? TRASLADO_STORAGE_KEY : esModoInventario ? INVENTARIO_STORAGE_KEY : STORAGE_KEY;
    const columnCatalog = esModoTraslado ? TRASLADO_OPTIONAL_COLS : OPTIONAL_COLS;
    const defaultVisible = esModoTraslado
      ? TRASLADO_DEFAULT_VISIBLE_OPTIONAL
      : esModoInventario
        ? INVENTARIO_DEFAULT_VISIBLE_OPTIONAL
        : mostrarStockPorDefecto
          ? new Set([...DEFAULT_VISIBLE_OPTIONAL, 'stock'])
          : DEFAULT_VISIBLE_OPTIONAL;
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return new Set(defaultVisible);
      const parsed = JSON.parse(saved) as Array<{ id: string; isVisible: boolean }>;
      const visible = new Set<string>();
      for (const col of parsed) {
        if (columnCatalog.some(c => c.id === col.id) && col.isVisible) {
          visible.add(col.id);
        }
      }
      return visible.size > 0 ? visible : new Set(defaultVisible);
    } catch {
      return new Set(defaultVisible);
    }
  });

  useEffect(() => {
    const storageKey = esModoTraslado ? TRASLADO_STORAGE_KEY : esModoInventario ? INVENTARIO_STORAGE_KEY : STORAGE_KEY;
    const columnCatalog = esModoTraslado ? [...FIXED_START, ...TRASLADO_OPTIONAL_COLS, ...FIXED_END.filter(c => c.id === 'accion')] : ALL_COLS;
    try {
      const data = columnCatalog.map(col => ({
        id: col.id,
        isVisible: col.isFixed ? true : visibleOptional.has(col.id),
      }));
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch { /* ignore */ }
  }, [visibleOptional, esModoTraslado, esModoInventario]);

  // ── Tamaño de la celda Precio U. (gestionado internamente, persistido en localStorage) ──
  const [priceDensity, setPriceDensity] = useState<CampoConSelectorDensity>(() => {
    try {
      const saved = localStorage.getItem(PRICE_DENSITY_STORAGE_KEY);
      return saved === 'extended' || saved === 'medium' || saved === 'compact' ? saved : DEFAULT_PRICE_DENSITY;
    } catch {
      return DEFAULT_PRICE_DENSITY;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PRICE_DENSITY_STORAGE_KEY, priceDensity);
    } catch { /* ignore */ }
  }, [priceDensity]);

  const [trasladoDensity, setTrasladoDensity] = useState<CampoConSelectorDensity>(() => {
    try {
      const saved = localStorage.getItem(TRASLADO_DENSITY_STORAGE_KEY);
      return saved === 'extended' || saved === 'medium' || saved === 'compact' ? saved : TRASLADO_DEFAULT_DENSITY;
    } catch {
      return TRASLADO_DEFAULT_DENSITY;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TRASLADO_DENSITY_STORAGE_KEY, trasladoDensity);
    } catch { /* ignore */ }
  }, [trasladoDensity]);

  // ── Click-to-edit de Subtotal/Total: por defecto solo lectura, se vuelve
  // input al hacer clic y regresa al diseño original al perder el foco ──
  const [editingSubtotalIds, setEditingSubtotalIds] = useState<Set<string>>(new Set());
  const [editingTotalIds, setEditingTotalIds] = useState<Set<string>>(new Set());

  const startEditingSubtotal = (itemKey: string) => {
    setEditingSubtotalIds(prev => new Set(prev).add(itemKey));
  };
  const stopEditingSubtotal = (itemKey: string) => {
    setEditingSubtotalIds(prev => {
      const next = new Set(prev);
      next.delete(itemKey);
      return next;
    });
  };
  const startEditingTotal = (itemKey: string) => {
    setEditingTotalIds(prev => new Set(prev).add(itemKey));
  };
  const stopEditingTotal = (itemKey: string) => {
    setEditingTotalIds(prev => {
      const next = new Set(prev);
      next.delete(itemKey);
      return next;
    });
  };

  // ── Columnas visibles ──
  const visibleCols: ColDef[] = useMemo(() => {
    let cols: ColDef[];
    if (esModoTraslado) {
      const accionCol = FIXED_END.find(c => c.id === 'accion')!;
      cols = [
        ...FIXED_START,
        ...TRASLADO_OPTIONAL_COLS.filter(c => visibleOptional.has(c.id)),
        accionCol,
      ];
    } else {
      const almacenCol: ColDef | null = getAlmacenOptionsForItem
        ? { id: 'almacen', label: 'Almacén', isFixed: true, isFixedEnd: false, align: 'left', minWidth: '160px' }
        : null;
      cols = [
        ...FIXED_START.slice(0, 2),
        ...(almacenCol ? [almacenCol] : []),
        ...FIXED_START.slice(2),
        ...OPTIONAL_COLS.filter(c => visibleOptional.has(c.id)),
        ...FIXED_END,
      ];
    }
    return mostrarColumnaAccion ? cols : cols.filter(c => c.id !== 'accion');
  }, [esModoTraslado, getAlmacenOptionsForItem, visibleOptional, mostrarColumnaAccion]);

  // ── Ancho mínimo de tabla (igual que TableAdvanced) para que sticky right:0 sea estable ──
  const tableMinWidth = useMemo(() =>
    visibleCols.reduce((sum, col) => {
      const px = parseInt(col.minWidth ?? col.width ?? '100', 10);
      return sum + (isNaN(px) ? 100 : px);
    }, 0),
  [visibleCols]);

  // ── th alignment helper ──
  const thAlign = (align: Align) =>
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  // ── renderCell (cierre sobre props) ──
  const renderCell = (col: ColDef, item: CartItem): React.ReactNode => {
    const itemKey = String(item.id);

    switch (col.id) {

      case 'producto':
        return (
          <td key={col.id} className="px-3 py-2.5 sticky left-0 z-[2] sticky-sep-r min-w-[170px] max-w-[230px]"
            style={{ background: 'var(--surface-0)' }}>
            <p className="line-clamp-2">{item.name}</p>
          </td>
        );

      case 'codigo':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.code ? <MonoBadge>{item.code}</MonoBadge> : <EmptyVal />}
          </td>
        );

      case 'almacen': {
        const almacenOptions = getAlmacenOptionsForItem?.(item) ?? [];
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            <select
              value={item.almacenId ?? ''}
              onChange={e => onAlmacenChange?.(item, e.target.value)}
              className="h-7 min-w-[140px] px-2 pr-6 border border-slate-200 rounded text-xs text-slate-700 bg-white cursor-pointer outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 appearance-none transition-[border-color,box-shadow] duration-150"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239299A8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 7px center',
              }}
            >
              {almacenOptions.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </td>
        );
      }

      case 'cantidad': {
        const bloqueado = esModoTraslado && soloLectura;
        const nc = esModoTraslado ? TRASLADO_NUMERIC_CELL_CLASSES[trasladoDensity] : TRASLADO_NUMERIC_CELL_CLASSES.medium;
        return (
          <td key={col.id} className="px-3 py-2">
            <div className="flex items-center justify-center gap-1">
              <button
                disabled={bloqueado || item.quantity <= 0}
                onClick={() => {
                  onClearQuantityDraft(itemKey);
                  onUpdateItem(item.id, { quantity: Math.max(0, parseFloat((item.quantity - 1).toFixed(8))) });
                }}
                className={`${nc.button} flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 text-base hover:bg-slate-100 hover:border-slate-300 hover:text-slate-800 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-100`}
              >−</button>
              <input
                type="number"
                min={0}
                step="any"
                value={quantityDrafts[itemKey] ?? formatQuantityDisplay(item.quantity)}
                onChange={e => onQuantityChange(itemKey, e.target.value)}
                onBlur={e => onQuantityBlur(item, e.target.value)}
                disabled={bloqueado}
                className={`w-[50px] ${nc.input} px-1.5 text-center border border-slate-200 rounded font-mono ${nc.text} font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:text-slate-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
              />
              <button
                disabled={bloqueado}
                onClick={() => {
                  onClearQuantityDraft(itemKey);
                  onUpdateItem(item.id, { quantity: parseFloat((item.quantity + 1).toFixed(8)) });
                }}
                className={`${nc.button} flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 text-base hover:bg-slate-100 hover:border-slate-300 hover:text-slate-800 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-100`}
              >+</button>
            </div>
          </td>
        );
      }

      case 'unidad': {
        const unitOpts = getUnitOptionsForItem(item);
        const currentCode = (item.unit ?? item.unidadMedida ?? '');
        const displayOpts = unitOpts.length > 0
          ? unitOpts
          : [{ code: currentCode, label: item.unitName ? `(${currentCode}) ${item.unitName}` : currentCode }];
        return (
          <td key={col.id} className="px-3 py-2">
            <select
              value={currentCode}
              onChange={e => onUnitChange(item, e.target.value)}
              disabled={esModoTraslado && soloLectura}
              className="h-7 min-w-[80px] px-2 pr-6 border border-slate-200 rounded text-xs text-slate-700 bg-white cursor-pointer outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 appearance-none disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed transition-[border-color,box-shadow] duration-150"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239299A8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 7px center',
              }}
            >
              {displayOpts.map(u => (
                <option key={u.code} value={u.code}>{u.label || u.code}</option>
              ))}
            </select>
          </td>
        );
      }

      case 'imagen':
        return (
          <td key={col.id} className="px-3 py-2 text-center">
            {item.imagen ? (
              <img src={item.imagen} alt={item.name} className="w-[34px] h-[34px] object-cover rounded border border-slate-200 mx-auto" />
            ) : (
              <Tooltip text="Sin imagen">
                <div className="w-[34px] h-[34px] bg-slate-100 border border-dashed border-slate-300 rounded flex items-center justify-center mx-auto text-slate-300">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </Tooltip>
            )}
          </td>
        );

      case 'alias':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.alias
              ? <span className="text-xs text-secondary">{item.alias}</span>
              : <EmptyVal />}
          </td>
        );

      case 'descripcion':
        return (
          <td key={col.id} className="px-3 py-2 min-w-[180px]">
            <textarea
              defaultValue={item.descripcion ?? ''}
              placeholder="Descripción del producto..."
              onBlur={e => onUpdateItem(item.id, { descripcion: e.target.value })}
              rows={2}
              className="w-full px-2 py-1 text-xs border border-slate-200 rounded bg-white text-slate-700 outline-none resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-[border-color,box-shadow] duration-150"
            />
          </td>
        );

      case 'categoria': {
        const categoryColor = getCategoryColorForItem?.(item);
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            {item.category
              ? <Badge color={categoryColor} variant={categoryColor ? undefined : 'gray'} noDot>{item.category}</Badge>
              : <EmptyVal />}
          </td>
        );
      }

      case 'marca':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.marca
              ? <span className="text-xs text-secondary">{item.marca}</span>
              : <EmptyVal />}
          </td>
        );

      case 'modelo':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.modelo ? <MonoBadge dim>{item.modelo}</MonoBadge> : <EmptyVal />}
          </td>
        );

      case 'tipoProducto':
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            {item.tipoProducto === 'BIEN'
              ? <InlineBadge className="bg-sky-50 text-sky-600 border border-sky-200">Bien</InlineBadge>
              : item.tipoProducto === 'SERVICIO'
                ? <InlineBadge className="bg-blue-50 text-blue-600 border border-blue-200">Servicio</InlineBadge>
                : <EmptyVal />}
          </td>
        );

      case 'tipoExistencia':
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            {item.tipoExistencia
              ? <Badge variant="amber" noDot>{TIPO_EXISTENCIA_LABELS[item.tipoExistencia] ?? item.tipoExistencia}</Badge>
              : <EmptyVal />}
          </td>
        );

      case 'codigoBarras':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.codigoBarras ? <MonoBadge>{item.codigoBarras}</MonoBadge> : <EmptyVal />}
          </td>
        );

      case 'codigoFabrica':
        return (
          <td key={col.id} className="px-3 py-2.5 whitespace-nowrap">
            {item.codigoFabrica ? <MonoBadge dim>{item.codigoFabrica}</MonoBadge> : <EmptyVal />}
          </td>
        );

      case 'codigoSunat': {
        if (esModoTraslado) {
          const habilitado = !soloLectura && Boolean(item.esNormalizado);
          return (
            <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
              {item.codigoSunat
                ? (
                  <InlineBadge className={habilitado
                    ? 'bg-amber-50 text-amber-600 border border-amber-200 font-mono text-[10px]'
                    : 'bg-slate-50 text-slate-300 border border-slate-200 font-mono text-[10px]'}
                  >
                    {item.codigoSunat}
                  </InlineBadge>
                )
                : <EmptyVal />}
            </td>
          );
        }
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            {item.codigoSunat
              ? <InlineBadge className="bg-amber-50 text-amber-600 border border-amber-200 font-mono text-[10px]">{item.codigoSunat}</InlineBadge>
              : <EmptyVal />}
          </td>
        );
      }

      case 'stock':
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            <StockBadge item={item} mode={stockDisplayMode} />
          </td>
        );

      case 'precioCompra':
        return (
          <td key={col.id} className="px-3 py-2.5 text-right whitespace-nowrap">
            {typeof item.precioCompra === 'number'
              ? <span className="font-mono text-[11.5px] text-tertiary">{formatBaseAsDocument(item.precioCompra)}</span>
              : <EmptyVal />}
          </td>
        );

      case 'descuento':
        return (
          <td key={col.id} className="px-3 py-2 text-center">
            <div className="relative inline-flex items-center">
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={item.descuentoProducto ?? 0}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v >= 0 && v <= 100) {
                    // descuentoItem: monto absoluto de descuento en términos de precio × línea
                    const descuentoItem = (item.price ?? 0) * item.quantity * v / 100;
                    onUpdateItem(item.id, { descuentoProducto: v, descuentoItem });
                  }
                }}
                className="w-[62px] h-7 pl-2 pr-5 border border-slate-200 rounded text-right font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="absolute right-2 text-[11px] font-semibold text-slate-400 pointer-events-none">%</span>
            </div>
          </td>
        );

      case 'peso': {
        if (esModoTraslado) {
          const nc = TRASLADO_NUMERIC_CELL_CLASSES[trasladoDensity];
          return (
            <td key={col.id} className="px-3 py-2 text-center">
              <div className="relative inline-flex items-center">
                <input
                  type="number"
                  min={0}
                  step={0.0001}
                  value={item.peso ?? ''}
                  onChange={e => onUpdateItem(item.id, { peso: e.target.value ? parseFloat(e.target.value) : undefined })}
                  disabled={soloLectura}
                  className={`w-[80px] ${nc.input} pl-2 pr-6 border border-slate-200 rounded font-mono ${nc.text} outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                />
                <span className="absolute right-2 text-[10.5px] font-semibold text-slate-400 pointer-events-none">kg</span>
              </div>
            </td>
          );
        }
        return (
          <td key={col.id} className="px-3 py-2 text-center">
            {item.peso != null ? (
              <div className="relative inline-flex items-center">
                <input
                  type="number"
                  defaultValue={item.peso}
                  min={0}
                  step={0.01}
                  onBlur={e => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v >= 0) onUpdateItem(item.id, { peso: v });
                  }}
                  className="w-[72px] h-7 pl-2 pr-6 border border-slate-200 rounded font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span className="absolute right-2 text-[10.5px] font-semibold text-slate-400 pointer-events-none">kg</span>
              </div>
            ) : <EmptyVal />}
          </td>
        );
      }

      case 'subpartida': {
        const deshabilitado = soloLectura || !item.esNormalizado;
        const opciones = getBienesNormalizadosOptionsForItem?.(item) ?? [];
        if (opciones.length > 0) {
          return (
            <td key={col.id} className="px-3 py-2">
              <select
                value={item.codigoSubpartidaNacional ?? ''}
                onChange={e => {
                  const seleccionado = opciones.find(o => o.subpartidaNacional === e.target.value);
                  onUpdateItem(item.id, {
                    codigoSubpartidaNacional: seleccionado?.subpartidaNacional,
                    codigoSunat: seleccionado?.codigoProductoSunat,
                  });
                }}
                disabled={deshabilitado}
                className="w-full h-7 px-2 border border-slate-200 rounded text-xs bg-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 transition-[border-color,box-shadow] duration-150"
              >
                <option value="">Seleccionar…</option>
                {opciones.map(o => (
                  <option key={o.subpartidaNacional} value={o.subpartidaNacional}>
                    {o.subpartidaNacional}{o.descripcion ? ` — ${o.descripcion}` : ''}
                  </option>
                ))}
              </select>
            </td>
          );
        }
        return (
          <td key={col.id} className="px-3 py-2">
            <input
              type="text"
              value={item.codigoSubpartidaNacional ?? ''}
              onChange={e => onUpdateItem(item.id, { codigoSubpartidaNacional: e.target.value || undefined })}
              disabled={deshabilitado}
              placeholder="Subpartida"
              className="w-full h-7 px-2 border border-slate-200 rounded font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 transition-[border-color,box-shadow] duration-150"
            />
          </td>
        );
      }

      case 'gtin':
        return (
          <td key={col.id} className="px-3 py-2">
            <input
              type="text"
              value={item.codigoGtin ?? ''}
              onChange={e => onUpdateItem(item.id, { codigoGtin: e.target.value || undefined })}
              disabled={soloLectura}
              placeholder="GTIN"
              className="w-full h-7 px-2 border border-slate-200 rounded font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 transition-[border-color,box-shadow] duration-150"
            />
          </td>
        );

      case 'normalizado':
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            <div className="flex items-center justify-center">
              <Checkbox
                checked={Boolean(item.esNormalizado)}
                disabled={soloLectura}
                onChange={e => onUpdateItem(item.id, { esNormalizado: e.target.checked })}
                size="sm"
              />
            </div>
          </td>
        );

      case 'impuesto': {
        const resolvedLabel = getImpuestoLabelForItem?.(item);
        const igvPercent = getIgvPercent(item);
        const igvType = item.igvType?.toUpperCase?.() ?? '';
        const cfg = IGV_LABELS[igvType];
        const fallbackLabel = igvPercent > 0
          ? `IGV (${igvPercent.toFixed(2)}%)`
          : igvType === 'INAFECTO' ? 'Inafecto (0.00%)' : 'Exonerado (0.00%)';
        const baseLabel = resolvedLabel ?? cfg?.label ?? item.impuesto ?? fallbackLabel;
        const subtitle = `(${igvPercent.toFixed(2)}%)`;
        const needsSubtitle = !resolvedLabel && Boolean(subtitle) && Boolean(baseLabel) && !baseLabel.toLowerCase().includes(subtitle.toLowerCase());
        const igvLabel = needsSubtitle ? `${baseLabel} ${subtitle}` : baseLabel;
        const badgeClassName = cfg?.className ?? 'bg-slate-100 text-slate-600 border border-slate-200';
        return (
          <td key={col.id} className="px-3 py-2.5 text-center whitespace-nowrap">
            <InlineBadge className={badgeClassName}>{igvLabel}</InlineBadge>
          </td>
        );
      }

      case 'precio': {
        const options = getPriceOptionsForItem(item);
        const hasOptions = options.length > 0;
        const selectedOptId = hasOptions
          ? (options.some(o => o.columnId === item.priceColumnId)
              ? item.priceColumnId ?? options[0].columnId
              : options[0].columnId)
          : null;
        const displayPrice = convertBaseToDocument(item.price ?? 0);
        const draftValue = priceDrafts[itemKey];
        const inputValue = draftValue ?? formatPrecioDinamico(displayPrice, decimalesPrecio);
        const liveValue = draftValue !== undefined
          ? (() => { const parsed = parseFloat(draftValue.replace(',', '.')); return Number.isNaN(parsed) ? null : parsed; })()
          : displayPrice;
        const errorMessage = priceErrors[itemKey]?.trim() ? priceErrors[itemKey] : undefined;
        const minValueDoc = typeof item.minAllowedPrice === 'number'
          ? convertBaseToDocument(item.minAllowedPrice)
          : null;
        const mappedOptions: CampoConSelectorOption[] = options.map(o => ({
          id: o.columnId,
          label: o.label,
          listValue: convertBaseToDocument(o.price),
          minValue: minValueDoc,
        }));
        const previousValueLabel = item.basePrice !== item.price
          ? formatBaseAsDocument(convertBaseToDocument(item.basePrice ?? 0))
          : undefined;

        const igvPct = getIgvPercent(item);
        const divisorIgv = igvPct > 0 ? 1 + igvPct / 100 : 1;
        const precioEntrada = item.price ?? 0;
        // companionValue and companionLabel are for display reference only
        void (precioIncluyeIgv ? precioEntrada / divisorIgv : precioEntrada * divisorIgv);

        return (
          <td key={col.id} className="px-3 py-2.5 text-right">
            <div className="flex justify-end">
              <CampoConSelector
                value={liveValue}
                rawInputValue={inputValue}
                onRawInputChange={raw => onPriceChange(itemKey, raw)}
                onInputBlur={raw => onPriceBlur(item, raw)}
                options={mappedOptions}
                selectedOptionId={selectedOptId}
                confirmed={hasOptions}
                onSelectOption={optionId => onPriceOptionChange(item, optionId, options)}
                autoFillOnSelect={false}
                staticOptionLabel={!hasOptions ? item.priceColumnLabel : undefined}
                errorMessage={errorMessage}
                manualEditBadge={hasOptions && item.isManualPrice}
                showWillSaveBadge={enablePriceLearning}
                previousValueLabel={previousValueLabel}
                fallbackMinValue={minValueDoc}
                prefix={currencyPrefix}
                density={priceDensity}
              />
            </div>
          </td>
        );
      }

      case 'subtotal': {
        const igvPercent = getIgvPercent(item);
        const divisorIgv = igvPercent > 0 ? 1 + igvPercent / 100 : 1;
        const precio = item.price ?? 0;
        const qty = item.quantity;
        const valorVentaTotal = permitirEdicionImportes
          ? calculateLineaComprobante(buildLinePricingInputFromCartItem(item, undefined, { priceIncludesTax: precioIncluyeIgv })).subtotal
          : (precioIncluyeIgv ? (precio / divisorIgv) * qty : precio * qty);

        if (!permitirEdicionImportes || !editingSubtotalIds.has(itemKey)) {
          return (
            <td key={col.id} className="px-3 py-2.5 text-right whitespace-nowrap">
              <span
                className={`font-mono text-secondary${permitirEdicionImportes ? ' cursor-pointer' : ''}`}
                onClick={permitirEdicionImportes ? () => startEditingSubtotal(itemKey) : undefined}
              >
                {formatBaseAsDocument(valorVentaTotal)}
              </span>
            </td>
          );
        }

        const draftValue = subtotalDrafts[itemKey];
        const inputValue = draftValue ?? convertBaseToDocument(valorVentaTotal).toFixed(2);

        return (
          <td key={col.id} className="px-3 py-2.5 text-right whitespace-nowrap">
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={inputValue}
              onChange={e => onSubtotalChange?.(itemKey, e.target.value)}
              onBlur={e => {
                onSubtotalBlur?.(item, e.target.value);
                stopEditingSubtotal(itemKey);
              }}
              className="w-[110px] h-8 px-2 border border-slate-200 rounded text-right font-mono text-secondary text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            />
          </td>
        );
      }

      case 'total': {
        const igvPercent = getIgvPercent(item);
        const divisorIgv = igvPercent > 0 ? 1 + igvPercent / 100 : 1;
        const precio = item.price ?? 0;
        const qty = item.quantity;
        const precioVentaTotal = permitirEdicionImportes
          ? calculateLineaComprobante(buildLinePricingInputFromCartItem(item, undefined, { priceIncludesTax: precioIncluyeIgv })).total
          : (precioIncluyeIgv ? precio * qty : precio * divisorIgv * qty);

        if (!permitirEdicionImportes || !editingTotalIds.has(itemKey)) {
          return (
            <td key={col.id} className="px-3 py-2.5 text-right whitespace-nowrap">
              <span
                className={`font-mono font-semibold${permitirEdicionImportes ? ' cursor-pointer' : ''}`}
                onClick={permitirEdicionImportes ? () => startEditingTotal(itemKey) : undefined}
              >
                {formatBaseAsDocument(precioVentaTotal)}
              </span>
            </td>
          );
        }

        const draftValue = totalDrafts[itemKey];
        const inputValue = draftValue ?? convertBaseToDocument(precioVentaTotal).toFixed(2);

        return (
          <td key={col.id} className="px-3 py-2.5 text-right whitespace-nowrap">
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={inputValue}
              onChange={e => onTotalChange?.(itemKey, e.target.value)}
              onBlur={e => {
                onTotalBlur?.(item, e.target.value);
                stopEditingTotal(itemKey);
              }}
              className="w-[110px] h-8 px-2 border border-slate-200 rounded text-right font-mono font-semibold text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            />
          </td>
        );
      }

      case 'accion':
        return (
          <td key={col.id} className="px-3 py-2 text-center sticky right-0 z-[1] sticky-sep-l"
            style={{ background: 'var(--surface-0)' }}>
            <DeleteConfirm onConfirm={() => onRemoveItem(item.id)} />
          </td>
        );

      default:
        return <td key={col.id} className="px-3 py-2.5"><EmptyVal /></td>;
    }
  };

  const columnDensity = esModoTraslado ? trasladoDensity : priceDensity;
  const setColumnDensity = esModoTraslado ? setTrasladoDensity : setPriceDensity;

  const columnPanel = (
    <CollapsiblePanel open={showColumnConfig}>
      {/* El espacio hacia el elemento de arriba (buscador) va DENTRO del área
          que anima su altura, no como margen externo que se prende/apaga de
          golpe — así el cierre se ve como un deslizado, sin salto. */}
      <div className="pt-3">
        <ColumnVisibilityPanel
          lockedColumns={esModoTraslado ? TRASLADO_LOCKED_COLUMNS_PANEL : LOCKED_COLUMNS_PANEL}
          columns={esModoTraslado ? TRASLADO_OPTIONAL_COLUMNS_PANEL : OPTIONAL_COLUMNS_PANEL}
          defaultVisible={[...visibleOptional]}
          onChange={ids => setVisibleOptional(new Set(ids))}
          onClose={onCloseColumnConfig}
          footerSlot={(
            <div className="flex items-center gap-2">
              <span className="text-ui-sm font-medium text-secondary">Presentación de columnas</span>
              <SegmentedToggle
                options={PRICE_DENSITY_OPTIONS.map(option => option.label)}
                value={PRICE_DENSITY_OPTIONS.find(option => option.value === columnDensity)?.label ?? PRICE_DENSITY_OPTIONS[0].label}
                onChange={label => {
                  const match = PRICE_DENSITY_OPTIONS.find(option => option.label === label);
                  if (match) setColumnDensity(match.value);
                }}
              />
            </div>
          )}
        />
      </div>
    </CollapsiblePanel>
  );

  return (
    <div>
      {/* Panel de columnas: se mantiene siempre montado (nunca se crea/destruye);
          es el CollapsiblePanel quien decide, con su prop `open`, si se ve
          expandido o colapsado. Si el padre nos da un contenedor, se pinta ahí
          (anidado visualmente en otra caja, ej. "Agregar producto"); si no, se
          pinta inline como bloque propio antes de la tabla. */}
      {columnPanelContainer
        ? createPortal(columnPanel, columnPanelContainer)
        : <div className="mb-3">{columnPanel}</div>}

      {/* Tabla */}
      <div className="bg-surface-0 border border-default rounded-lg overflow-hidden">
        <div className="overflow-auto relative" style={{ maxHeight }}>
          <table className="w-full border-collapse text-sm" style={{ minWidth: `${tableMinWidth}px` }}>

              {/* THEAD */}
              <thead className="bg-surface-1 border-b border-default sticky top-0 z-20">
                <tr>
                  {visibleCols.map(col => (
                    <th
                      key={col.id}
                      className={[
                        'px-3 py-2.5 text-caption-medium font-sans',
                        'text-tertiary whitespace-nowrap select-none',
                        thAlign(col.align),
                        col.id === 'producto' ? 'sticky left-0 bg-surface-1 z-[22] sticky-sep-r' : '',
                        col.id === 'accion'   ? 'sticky right-0 bg-surface-1 z-10 sticky-sep-l' : '',
                      ].join(' ')}
                      style={{
                        width: col.width,
                        minWidth: col.minWidth,
                      }}
                    >
                      {col.id === 'subtotal'
                        ? <span>Subtotal <span className="bg-slate-100 border border-slate-200 rounded px-1 py-px text-[9.5px] font-semibold text-slate-400 tracking-[0.04em] align-middle">s/IGV</span></span>
                        : col.id === 'precio'
                          ? (precioIncluyeIgv ? 'Precio unitario' : 'Valor U.')
                          : col.id === 'producto' && productoColumnLabel
                            ? productoColumnLabel
                            : col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* TBODY */}
              <tbody className="text-ui-sm font-sans text-primary">
                {items.length === 0 ? (
                  <EmptyState
                    colSpan={visibleCols.length}
                    onAdd={onAddProduct}
                    title={emptyStateTitle}
                    subtitle={emptyStateSubtitle}
                    buttonLabel={emptyStateButtonLabel}
                  />
                ) : (
                  items.map(item => (
                    <tr
                      key={item.id}
                      className={`border-b border-muted last:border-b-0 hover:bg-blue-50/30 focus-within:shadow-[inset_2px_0_0_theme(colors.blue.400)] transition-colors duration-100 ${
                        item.id === lastAddedProductId ? 'bg-blue-50' : ''
                      }`}
                    >
                      {visibleCols.map(col => (
                        <React.Fragment key={`${item.id}-${col.id}`}>
                          {renderCell(col, item)}
                        </React.Fragment>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>

            </table>
        </div>
      </div>
    </div>
  );
};

export default TablaCatalogo;
